import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coffee, TrendingUp, Calculator, CheckCircle } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brew-business-value/artifacts/t1741byz_Colour%20Landscape%20Logo.png";

const ProcessingScreen = () => {
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        { icon: Coffee, text: "Analysing café performance...", color: "text-primary" },
        { icon: TrendingUp, text: "Comparing to industry benchmarks...", color: "text-secondary" },
        { icon: Calculator, text: "Calculating valuation...", color: "text-green-600" }
    ];

    useEffect(() => {
        const timer1 = setTimeout(() => setCurrentStep(1), 1000);
        const timer2 = setTimeout(() => setCurrentStep(2), 2000);
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="processing-screen">
            {/* Logo */}
            <motion.img 
                src={LOGO_URL} 
                alt="Bonza" 
                className="h-16 mb-12 object-contain"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            />

            {/* Loading Animation */}
            <motion.div
                className="relative w-24 h-24 mb-12"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
            >
                {/* Outer ring */}
                <div className="absolute inset-0 rounded-full border-4 border-gray-100" />
                
                {/* Animated ring */}
                <motion.div
                    className="absolute inset-0 rounded-full border-4 border-t-primary border-r-secondary border-b-transparent border-l-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                />
                
                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <Coffee className="w-10 h-10 text-primary" />
                </div>
            </motion.div>

            {/* Processing Steps */}
            <div className="space-y-6 max-w-sm w-full">
                {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isActive = currentStep >= index;
                    const isComplete = currentStep > index;

                    return (
                        <motion.div
                            key={index}
                            className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
                                isActive ? "bg-gray-50" : "bg-transparent"
                            }`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ 
                                opacity: isActive ? 1 : 0.4, 
                                x: 0 
                            }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                                isComplete 
                                    ? "bg-green-100" 
                                    : isActive 
                                        ? "bg-primary/10" 
                                        : "bg-gray-100"
                            }`}>
                                {isComplete ? (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                    <StepIcon className={`w-5 h-5 ${isActive ? step.color : "text-gray-400"}`} />
                                )}
                            </div>
                            <span className={`font-medium transition-colors duration-300 ${
                                isActive ? "text-gray-900" : "text-gray-400"
                            }`}>
                                {step.text}
                            </span>
                        </motion.div>
                    );
                })}
            </div>

            {/* Loading dots */}
            <motion.div
                className="flex items-center gap-2 mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
            >
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        className="w-2 h-2 rounded-full bg-primary"
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.5, 1, 0.5]
                        }}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: i * 0.2
                        }}
                    />
                ))}
            </motion.div>
        </div>
    );
};

export default ProcessingScreen;
